import { PermRole, RoleAccess, RoleAppNav } from '@/models/user-data.model';
import { PermsService } from '@/services/api/perms.service';
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, effect, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AccessPermission } from 'src/app/models/access-permission';
import { AppNav } from 'src/app/models/app-nav';
import { PickListModule } from 'primeng/picklist';
import { ScrollerModule } from 'primeng/scroller';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { RolesService } from '@/services/api/roles.service';


@Component({
  selector: 'app-role-editor',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, PickListModule, ScrollerModule, ButtonModule, InputTextModule],
  template: `
  <div class="role-editor-container" *ngIf="selectedRole">
    <!-- Role Title Section -->
    <div class="role-title-section mb-4">
      <div class="flex align-items-center gap-3">
        <span class="role-title-label">Role Title</span>
        <span class="role-title-value" *ngIf="!editRoleTitle">{{ selectedRole.RoleName }}</span>
        <input 
          *ngIf="editRoleTitle"
          pInputText 
          id="title" 
          [(ngModel)]="selectedRole.RoleName" 
          placeholder="Enter a name for this role."
          class="role-title-input"
          (blur)="editRoleTitle = false"
          (keyup.enter)="editRoleTitle = false"
        />
        <button 
          pButton 
          pRipple 
          [icon]="editRoleTitle ? 'pi pi-times' : 'pi pi-pencil'" 
          class="p-button-rounded p-button-success p-button-text edit-title-btn"
          (click)="toggleEditRoleTitle()"
          [title]="editRoleTitle ? 'Cancel' : 'Edit Role Title'"
        ></button>
      </div>
    </div>

    <!-- Navigation Items and API Permissions Sections -->
    <div class="sections-container" *ngIf="!editPerms && !editNav">
      <div class="section-wrapper">
        <div class="section-header">
          <div class="flex align-items-center gap-2">
            <i class="pi pi-list section-icon"></i>
            <h4 class="section-title m-0">Navigation Items</h4>
          </div>
          <button 
            pButton 
            pRipple 
            icon="pi pi-pencil" 
            class="p-button-rounded p-button-success p-button-text edit-section-btn"
            (click)="showNav()"
            title="Edit Navigation Items"
          ></button>
        </div>
        <div class="section-content">
          <p-scroller 
            [items]="targetNavs" 
            [itemSize]="50" 
            scrollHeight="400px" 
            styleClass="border-1 surface-border" 
            class="itemList"
          >
            <ng-template pTemplate="item" let-nav let-options="options">
              <div class="flex align-items-center p-2 item-row" [ngClass]="{ 'surface-ground': options.odd }" style="height: 50px;">
                <i [class]="nav?.icon" class="mr-2"></i>
                <span>{{ nav?.label }}</span>
              </div>
            </ng-template>
          </p-scroller>
        </div>
      </div>

      <div class="section-wrapper">
        <div class="section-header">
          <div class="flex align-items-center gap-2">
            <i class="pi pi-lock section-icon"></i>
            <h4 class="section-title m-0">API Permissions</h4>
          </div>
          <button 
            pButton 
            pRipple 
            icon="pi pi-pencil" 
            class="p-button-rounded p-button-success p-button-text edit-section-btn"
            (click)="showPerms()"
            title="Edit API Permissions"
          ></button>
        </div>
        <div class="section-content">
          <p-scroller 
            [items]="targetPerms" 
            [itemSize]="50" 
            scrollHeight="400px" 
            styleClass="border-1 surface-border" 
            class="itemList"
          >
            <ng-template pTemplate="item" let-perm let-options="options">
              <div class="flex align-items-center p-2 item-row" [ngClass]="{ 'surface-ground': options.odd }" style="height: 50px;">
                {{ perm?.PermTitle }}
              </div>
            </ng-template>
          </p-scroller>
        </div>
      </div>
    </div>

    <!-- Edit Permissions PickList -->
    <div *ngIf="editPerms" class="mb-4">
      <div class="section-header mb-3">
        <div class="flex align-items-center gap-2">
          <i class="pi pi-lock section-icon"></i>
          <h4 class="section-title m-0">API Permissions</h4>
        </div>
        <button 
          pButton 
          pRipple 
          icon="pi pi-times" 
          class="p-button-rounded p-button-success p-button-text edit-section-btn"
          (click)="showPerms()"
          title="Cancel Editing"
        ></button>
      </div>
      <p-pickList 
        [source]="sourcePerms" 
        [target]="targetPerms" 
        sourceHeader="Available" 
        targetHeader="Selected" 
        [dragdrop]="false" 
        [responsive]="true" 
        [sourceStyle]="{ height: '30rem' }"
        [targetStyle]="{ height: '30rem' }" 
        filterBy="PermTitle" 
        sourceFilterPlaceholder="Search by name" 
        targetFilterPlaceholder="Search by name" 
        breakpoint="1400px"
      >
        <ng-template let-permission pTemplate="item">
          <div class="p-2">
            <span>{{ permission?.PermTitle }}</span>
          </div>
        </ng-template>
      </p-pickList>
    </div>
    
    <!-- Edit Navigation PickList -->
    <div *ngIf="editNav" class="mb-4">
      <div class="section-header mb-3">
        <div class="flex align-items-center gap-2">
          <i class="pi pi-list section-icon"></i>
          <h4 class="section-title m-0">Navigation Items</h4>
        </div>
        <button 
          pButton 
          pRipple 
          icon="pi pi-times" 
          class="p-button-rounded p-button-success p-button-text edit-section-btn"
          (click)="showNav()"
          title="Cancel Editing"
        ></button>
      </div>
      <p-pickList 
        [source]="sourceNavs" 
        [target]="targetNavs" 
        sourceHeader="Available" 
        targetHeader="Selected" 
        [dragdrop]="false" 
        [responsive]="true" 
        [sourceStyle]="{ height: '30rem' }"
        [targetStyle]="{ height: '30rem' }" 
        filterBy="label" 
        sourceFilterPlaceholder="Search by name" 
        targetFilterPlaceholder="Search by name" 
        breakpoint="1400px"
      >
        <ng-template let-nav pTemplate="item">
          <div class="p-2 flex align-items-center">
            <i [class]="nav?.icon" class="mr-2"></i>
            <span>{{ nav?.label }}</span>
          </div>
        </ng-template>
      </p-pickList>
    </div>

    <!-- Footer Save Button -->
    <div class="footer-buttons mt-4">
      <button 
        pButton 
        pRipple 
        label="Save" 
        icon="pi pi-check" 
        class="p-button-success save-btn"
        (click)="saveRoleEntry()"
      ></button>
    </div>
  </div>
  `,

  styles: `
  .role-editor-container {
    padding: 1rem;
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .role-title-section {
    padding-bottom: 1rem;
    border-bottom: 1px solid var(--surface-border);
  }

  .role-title-label {
    font-weight: 600;
    font-size: 1rem;
  }

  .role-title-value {
    font-size: 1rem;
    color: var(--text-color);
  }

  .role-title-input {
    flex: 1;
    max-width: 400px;
  }

  .edit-title-btn {
    margin-left: auto;
  }

  .section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.75rem;
  }

  .section-icon {
    font-size: 1.25rem;
    color: var(--text-color-secondary);
  }

  .section-title {
    font-size: 1rem;
    font-weight: 600;
  }

  .edit-section-btn {
    flex-shrink: 0;
  }

  .sections-container {
    display: flex;
    gap: 1.5rem;
    flex: 1;
    min-height: 0;
  }

  .section-wrapper {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .section-content {
    margin-top: 0.5rem;
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  .itemList {
    width: 100%;
    height: 100%;
    flex: 1;
  }

  .item-row {
    min-height: 50px;
  }

  .footer-buttons {
    margin-top: auto;
    padding-top: 1rem;
    border-top: 1px solid var(--surface-border);
  }

  .save-btn {
    min-width: 100px;
  }

  @media (max-width: 768px) {
    .sections-container {
      flex-direction: column;
    }
  }
  `
})
export class RoleEditorComponent implements OnInit, OnChanges {
  selectedRole: PermRole | null = null;
  @Input() navItems: AppNav[] = [];
  @Input() editMode: boolean = false;
  @Output() selectedRoleChange= new EventEmitter<PermRole>();
  @Output() submitted = new EventEmitter;
  editNav = false;
  editPerms = false;
  editRoleTitle = false;

  initialTargetPerms: RoleAccess[] = [];
  sourcePerms: AccessPermission[]=[]
  targetPerms: AccessPermission[]=[]

  initialTargetNavs: RoleAppNav[] = [];
  sourceNavs: AppNav[]=[]
  targetNavs: AppNav[]=[]

  constructor(
    private permsService: PermsService,
    private rolesService: RolesService,
    private cdr: ChangeDetectorRef
    ) { 
        effect(() => {
            const role = this.rolesService.selectedRole();
            if(role) {
                this.selectedRole = role;
            }
        });
    }

  ngOnInit(): void {
    this.collectPermItems();
    this.collectNavItems();
  }

  toggleEditRoleTitle() {
    this.editRoleTitle = !this.editRoleTitle;
  }

  showNav() {
    if(!this.editNav) {
      this.editNav = true;
      this.editPerms = false;
    } else {
      this.editNav = false;
      this.editPerms = false;
    }
  }

  showPerms() {
    if(!this.editPerms) {
      this.editPerms = true;
      this.editNav = false;
    } else {
      this.editNav = false;
      this.editPerms = false;
    }
  }


  ngOnChanges(changes: SimpleChanges) {
    if (changes['editMode'] && changes['editMode'].currentValue === true) {
      console.log('edit change detected')
      this.collectPermItems();
      this.collectNavItems();
    }
  }

  resetForm() {
    this.collectPermItems();
    this.editNav = false;
    this.editPerms = false;
  }

  collectPermItems() {
    this.permsService.getPerms().subscribe(data => {
      this.sourcePerms = data;
      this.setPermTargets();
      this.removePermTargetsFromSource();
      this.cdr.markForCheck();
    });
  }

  collectNavItems() {
    this.permsService.getNavs().subscribe(data => {
      this.sourceNavs = data;
      this.setNavTargets();
      this.removeNavTargetsFromSource();
      this.cdr.markForCheck();
    });
  }
  
  removeNavTargetsFromSource() {
    this.sourceNavs = this.sourceNavs.filter(sourceNav => 
      !this.targetNavs.some(targetNav => targetNav && targetNav.appNavId === sourceNav.appNavId)
    );
  }

  setNavTargets() {
    if(this.selectedRole?.roleAppNav){
      this.targetNavs = this.selectedRole.roleAppNav.flatMap(roleNav => roleNav.appNav);
      this.initialTargetNavs = this.selectedRole.roleAppNav;
    }
    else {
      this.targetPerms =[];
      this.initialTargetPerms = [];
    }
  }
    
  setPermTargets() {
    if(this.selectedRole?.RoleAccess){
      this.targetPerms = this.selectedRole.RoleAccess.flatMap(roleAccess => roleAccess.AccessPermissions);
      this.initialTargetPerms = this.selectedRole.RoleAccess;
    }
    else {
      this.targetPerms =[];
      this.initialTargetPerms = [];
    }
  }
  
  removePermTargetsFromSource() {
    this.sourcePerms = this.sourcePerms.filter(sourcePerm => 
      !this.targetPerms.some(targetPerm => targetPerm && targetPerm.AccessPermID === sourcePerm.AccessPermID)
    );
  }

  saveRoleEntry() {
    const permConnect = this.targetPerms.filter(
      perm => !this.initialTargetPerms.some(p => p.AccessPermID === perm.AccessPermID)
    ).map(perm => ({ AccessPermID: perm.AccessPermID }));
  
    const permDisconnect = this.initialTargetPerms.filter(
      perm => !this.targetPerms.some(p => p.AccessPermID === perm.AccessPermID)
    ).map(perm => ({ RoleAccessID: perm.RoleAccessID }));

    const navConnect = this.targetNavs.filter(
      nav => !this.initialTargetNavs.some(n => n.appNavId === nav.appNavId)
    ).map(nav => ({ appNavId: nav.appNavId }));
  
    const navDisconnect = this.initialTargetNavs.filter(
      nav => !this.targetNavs.some(n => n.appNavId === nav.appNavId)
    ).map(nav => ({ roleAppNav: nav.roleAppNavId }));
  
    const RoleAccess: any = {};
    const roleAppNav: any = {};

  if (permConnect.length > 0) {
    RoleAccess.connect = permConnect;
  }

  if (permDisconnect.length > 0) {
    RoleAccess.disconnect = permDisconnect;
  }
  
  if (navConnect.length > 0) {
    roleAppNav.connect = navConnect;
  }

  if (navDisconnect.length > 0) {
    roleAppNav.disconnect = navDisconnect;
  }
  

  const updateData = {
    RoleName: this.selectedRole?.RoleName,
    RoleAccess: RoleAccess,
    roleAppNav: roleAppNav
  };
  
    this.permsService.updateRole(this.selectedRole?.PermRoleID ?? 0, updateData).subscribe();
    this.submitted.emit();
  }
}
